import { Avatar, AvatarFallback } from "@bowfast/ui/components/avatar";
import type { BadgeProps } from "@bowfast/ui/components/badge";
import { Badge } from "@bowfast/ui/components/badge";
import { cn } from "@bowfast/ui/lib/utils";
import { ArrowRightIcon, MoreVerticalIcon } from "lucide-react";
import type * as React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExpeditionStatus = "draft" | "planning" | "open" | "active" | "completed" | "cancelled";

/** Map status → 4px top bar colour (matches design-system CSS vars) */
const STATUS_BAR: Record<ExpeditionStatus, string> = {
  draft: "#cbd5e1",
  planning: "#cbd5e1",
  open: "#047cc7",
  active: "#10b981",
  completed: "#8b5cf6",
  cancelled: "#ef4444",
};

/** Map status → Badge variant */
const STATUS_VARIANT: Record<ExpeditionStatus, BadgeProps["variant"]> = {
  draft: "draft",
  planning: "planning",
  open: "open",
  active: "active",
  completed: "completed",
  cancelled: "cancelled",
};

export interface ExpeditionStat {
  label: string;
  value: string;
  muted?: boolean;
}

export interface CrewMember {
  /** Two-letter initials shown as avatar fallback */
  initials: string;
}

export interface ExpeditionCardProps extends React.ComponentProps<"article"> {
  /** Expedition name */
  title: string;
  /** e.g. "Lisbon → Barbados" */
  route: string;
  /** Current lifecycle status */
  status: ExpeditionStatus;
  /** Optional type badge label (e.g. "Bluewater", "Coastal", "ARC") */
  type?: string;
  /** Up to 4 stat cells shown in the grid strip */
  stats?: ExpeditionStat[];
  /** Vessel display: flag emoji + name, e.g. { flag: "🇵🇹", name: "SY Aventura II" } */
  vessel?: { flag?: string; name: string };
  /** Crew members to show in the stacked avatar row */
  crew?: CrewMember[];
  /** If crew is larger than displayed, show a "+N" overflow bubble */
  crewOverflow?: number;
  /** Called when the ⋮ menu button is pressed */
  onMenuClick?: (e: React.MouseEvent) => void;
}

// ─── CrewStack ────────────────────────────────────────────────────────────────
// Extracted from .crew-stack / .crew-avatar in expeditions-list-cards.html

function CrewStack({ crew, overflow }: { readonly crew: CrewMember[]; readonly overflow?: number }) {
  return (
    <div className="flex items-center">
      {crew.map((member, i) => (
        <Avatar
          key={member.initials}
          size="sm"
          className={cn(
            // 2px card-bg border + -0.5rem overlap, matching .crew-avatar
            "size-7 border-2 border-card",
            i > 0 && "-ml-2",
          )}
        >
          <AvatarFallback className="bg-abyss-100 text-abyss-700 text-[0.5625rem] font-bold dark:bg-abyss-800 dark:text-abyss-200">
            {member.initials}
          </AvatarFallback>
        </Avatar>
      ))}
      {overflow != null && overflow > 0 && (
        <Avatar size="sm" className="-ml-2 size-7 border-2 border-card">
          <AvatarFallback className="bg-slate-200 text-slate-600 text-[0.5625rem] font-bold dark:bg-slate-700 dark:text-slate-300">
            +{overflow}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}

// ─── StatStrip ────────────────────────────────────────────────────────────────
// Extracted from .exp-card__stats / .stat-item in expeditions-list-cards.html

function StatStrip({ stats }: { readonly stats: ExpeditionStat[] }) {
  return (
    <div className="grid grid-cols-4 border-y border-border bg-muted/30">
      {stats.map((stat, i) => (
        <div
          key={stat.label}
          className={cn("flex flex-col gap-1.5 px-3.5 py-3.5", i < stats.length - 1 && "border-r border-border")}
        >
          {/* .stat-label: 9px, 700, uppercase, 0.07em tracking */}
          <span className="text-[0.5625rem] font-bold uppercase tracking-[0.07em] text-muted-foreground">
            {stat.label}
          </span>
          {/* .stat-value: 15px, 700, tabular-nums */}
          <span
            className={cn(
              "truncate text-[0.9375rem] font-bold tabular-nums leading-tight",
              stat.muted ? "font-normal text-muted-foreground" : "text-foreground",
            )}
          >
            {stat.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── ExpeditionCard ───────────────────────────────────────────────────────────

export function ExpeditionCard({
  title,
  route,
  status,
  type,
  stats = [],
  vessel,
  crew = [],
  crewOverflow,
  onMenuClick,
  className,
  ...props
}: Readonly<ExpeditionCardProps>) {
  const barColor = STATUS_BAR[status];
  const badgeVariant = STATUS_VARIANT[status];
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <article
      className={cn(
        // .exp-card
        "group/exp relative flex flex-col overflow-hidden rounded-lg",
        "border-[1.5px] border-border bg-card",
        "shadow-[0_2px_8px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.04)]",
        "transition-[box-shadow,transform,border-color] duration-150 ease-out",
        "cursor-pointer",
        // .exp-card:hover
        "hover:-translate-y-0.5 hover:border-primary hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]",
        className,
      )}
      {...props}
    >
      {/* 4px status bar — .exp-card__bar */}
      <span className="h-1 w-full shrink-0" style={{ background: barColor }} aria-hidden />

      {/* Head — .exp-card__head */}
      <div className="flex items-start justify-between gap-2 px-5 pt-4.5 pb-4">
        {/* Left: title + route — .exp-card__head-left */}
        <div className="min-w-0 flex-1">
          {/* .exp-card__title: Bricolage Grotesque 17px 700 */}
          <p className="mb-1.5 truncate font-heading text-[1.0625rem] font-bold leading-tight text-foreground">
            {title}
          </p>
          {/* .exp-card__route: 13px muted with arrow icon */}
          <p className="flex items-center gap-1 text-[0.8125rem] text-muted-foreground">
            <ArrowRightIcon className="size-3 shrink-0" aria-hidden />
            {route}
          </p>
        </div>

        {/* Right: badges + menu ── always in-flow, no absolute overlap */}
        <div className="mt-0.5 flex shrink-0 items-start gap-1.5">
          <div className="flex flex-wrap justify-end gap-1">
            <Badge variant={badgeVariant} dotted={status === "active" || status === "open"} pulse={status === "active"}>
              {statusLabel}
            </Badge>
            {type && (
              <Badge variant="outline" className="max-w-[8rem]">
                <span className="truncate">{type}</span>
              </Badge>
            )}
          </div>
          <button
            type="button"
            aria-label={`More options for ${title}`}
            onClick={onMenuClick}
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-md",
              "text-muted-foreground/50 transition-[background,color] duration-100",
              "opacity-0 group-hover/exp:opacity-100",
              "hover:bg-muted hover:text-foreground",
            )}
          >
            <MoreVerticalIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* 4-stat strip — .exp-card__stats */}
      {stats.length > 0 && <StatStrip stats={stats} />}

      {/* Footer — .exp-card__footer */}
      <div className="mt-auto flex min-h-14 items-center justify-between px-5 py-3.5">
        {/* Vessel — .exp-card__vessel */}
        {vessel ? (
          <p className="flex items-center gap-1 text-[0.8125rem] text-muted-foreground">
            {vessel.flag && <span aria-hidden>{vessel.flag}</span>}
            <strong className="font-medium text-foreground">{vessel.name}</strong>
          </p>
        ) : (
          <p className="text-[0.8125rem] text-muted-foreground">No vessel yet</p>
        )}

        {/* Crew stack — .exp-card__crew-row + .crew-stack */}
        {crew.length > 0 && <CrewStack crew={crew} {...(crewOverflow == null ? {} : { overflow: crewOverflow })} />}
      </div>
    </article>
  );
}
